import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import config from "../../config";
import "./ForgetPassword.css";

const ForgetPasswordOne = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";

  useEffect(() => {
    if (!email) {
      navigate("/forget-password");
    }
  }, [email, navigate]);

  const initialValues = {
    token: "",
    password: "",
    confirmPassword: "",
  };

  const validationSchema = Yup.object({
    token: Yup.string().required("OTP/Token is required"),
    password: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/,
        "Password must meet complexity requirements."
      )
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setErrorMessage("");
    setSuccessMessage("");
    try {
      // 1. Verify OTP first
      const verifyRes = await fetch(`${config.API_BASE_URL}${config.OTP_VERIFY_ENDPOINT}?email=${encodeURIComponent(email)}&otp=${encodeURIComponent(values.token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      if (!verifyRes.ok) {
        const errorData = await verifyRes.json().catch(() => ({}));
        setErrorMessage(errorData.message || "Invalid OTP. Please try again.");
        setSubmitting(false);
        return;
      }

      // 2. If OTP is valid, call reset password
      const response = await fetch(`${config.API_BASE_URL}${config.RESET_PASSWORD_ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          newPassword: values.password,
          confirmPassword: values.confirmPassword,
        }),
      });

      if (response.ok) {
        setSuccessMessage("Password reset successful. Redirecting to login...");
        setTimeout(() => {
          navigate("/signin");
        }, 2000);
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.message || "Failed to reset password. Token might be invalid or expired.");
      }
    } catch (error) {
      console.error("Reset password error:", error);
      setErrorMessage("Network error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!email) return null;

  return (
    <div className='forget-password-container'>
      <div className='forget-password-box'>
        <div>
          <div style={{ fontSize: "24px", fontWeight: "600", marginBottom: "20px" }}>Reset Password</div>
        </div>
        
        {successMessage && <div style={{ color: "green", marginBottom: "10px" }}>{successMessage}</div>}
        {errorMessage && <div style={{ color: "red", marginBottom: "10px" }}>{errorMessage}</div>}

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className='forget-password-form'>
              <div className='input-group'>
                <label>Email ID</label>
                <div style={{ padding: "12px 16px", backgroundColor: "#f5f5f5", borderRadius: "8px", color: "#555", border: "1px solid #ddd", fontSize: "14px" }}>
                  {email}
                </div>
              </div>

              <div className='input-group'>
                <label>OTP / Token</label>
                <Field type='text' name='token' placeholder='Enter token received in email' />
                <ErrorMessage name='token' component='div' className='error' />
              </div>

              <div className='input-group'>
                <label>New Password</label>
                <div className='password-wrapper'>
                  <Field
                    type={showPassword ? "text" : "password"}
                    name='password'
                    placeholder='Enter new password'
                  />
                  <span
                    className='toggle-password'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <IoEyeOutline size={18} /> : <IoEyeOffOutline size={18} />}
                  </span>
                  <span
                    className='info-icon-password'
                    onClick={() => setShowPopup(!showPopup)}
                    title='Password Requirements'
                  >
                    ℹ
                  </span>
                  {showPopup && (
                    <div className='password-popup'>
                      <p>
                        <strong>Password Requirement</strong>
                      </p>
                      <ul>
                        <li>8 to 16 characters</li>
                        <li>Uppercase</li>
                        <li>Lowercase</li>
                        <li>Number</li>
                        <li>Special character</li>
                      </ul>
                    </div>
                  )}
                </div>
                <ErrorMessage
                  name='password'
                  component='div'
                  className='error'
                />
              </div>

              <div className='input-group'>
                <label>Confirm Password</label>
                <div className='password-wrapper'>
                  <Field
                    type={showConfirmPassword ? "text" : "password"}
                    name='confirmPassword'
                    placeholder='Confirm new password'
                  />
                  <span
                    className='toggle-password'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <IoEyeOutline size={18} /> : <IoEyeOffOutline size={18} />}
                  </span>
                </div>
                <ErrorMessage
                  name='confirmPassword'
                  component='div'
                  className='error'
                />
              </div>

              <button
                type='submit'
                disabled={isSubmitting}
                className='submit-button'
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ForgetPasswordOne;
